import { useState, useCallback } from 'react';
import * as mammoth from 'mammoth';

// ─── Constants ───────────────────────────────────────────────────────────────

const SUPPORTED_EXTENSIONS = ['txt', 'pdf', 'docx'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Read a File object into an ArrayBuffer.
 */
const readFileAsArrayBuffer = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = (e) => resolve(e.target.result);
    reader.onerror = ()  => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });

/**
 * Read a File object as plain text (UTF-8).
 */
const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = (e) => resolve(e.target.result);
    reader.onerror = ()  => reject(new Error('Failed to read file as text'));
    reader.readAsText(file, 'UTF-8');
  });

/**
 * Detect file type from the first few magic bytes of an ArrayBuffer.
 * Mirrors what fileTypeFromStream() does on the Node.js side.
 */
const detectFileTypeFromBuffer = (arrayBuffer) => {
  const bytes = new Uint8Array(arrayBuffer.slice(0, 8));

  // PDF: starts with %PDF  (hex: 25 50 44 46)
  if (bytes[0] === 0x25 && bytes[1] === 0x50 &&
      bytes[2] === 0x44 && bytes[3] === 0x46) {
    return 'pdf';
  }

  // DOCX / ZIP:  PK magic bytes (hex: 50 4B 03 04)
  // DOCX files are ZIP archives, so we check the ZIP header.
  if (bytes[0] === 0x50 && bytes[1] === 0x4B &&
      bytes[2] === 0x03 && bytes[3] === 0x04) {
    return 'docx';
  }

  return null; // unknown
};

/**
 * Extract plain text from a PDF ArrayBuffer using pdf.js (pdfjs-dist).
 * Install:  npm install pdfjs-dist
 */
const extractTextFromPdf = async (arrayBuffer) => {
  const pdfjsLib = await import('pdfjs-dist');
  pdfjsLib.GlobalWorkerOptions.workerSrc =
    `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

  const pdf   = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const pages = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page    = await pdf.getPage(i);
    const content = await page.getTextContent();

    let pageText    = '';
    let lastY       = null;
    let lastX       = null;

    for (const item of content.items) {
      const currentY = item.transform[5]; // Y position of this text item
      const currentX = item.transform[4]; // X position of this text item

      if (lastY === null) {
        // First item on the page
        pageText += item.str;
      } else {
        const yDiff = Math.abs(lastY - currentY);

        if (yDiff > 10) {
          // Large Y gap = new paragraph → double newline
          pageText += '\n\n' + item.str;
        } else if (yDiff > 2) {
          // Small Y gap = new line → single newline
          pageText += '\n' + item.str;
        } else if (currentX > lastX + 5) {
          // Same line, gap between words → add space
          pageText += ' ' + item.str;
        } else {
          // Continuous text on same line
          pageText += item.str;
        }
      }

      lastY = currentY;
      lastX = currentX + (item.width ?? 0);
    }

    pages.push(pageText);
  }

  // Join pages with double newline to separate them
  return pages.join('\n\n');
};

/**
 * Extract plain text from a DOCX ArrayBuffer using mammoth.
 * Install:  npm install mammoth
 */
const extractTextFromDocx = async (arrayBuffer) => {
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value;
};

// ─── Main extractor ──────────────────────────────────────────────────────────

/**
 * Given a browser File object, returns its full plain-text content.
 * Mirrors the Node.js logic exactly:
 *   .txt  → read as UTF-8 text
 *   .pdf  → detect via magic bytes → extract via pdf.js
 *   .docx → detect via magic bytes → extract via mammoth
 *
 * @param  {File}   file
 * @returns {Promise<string>}
 */
export const extractTextFromFile = async (file) => {
  const extension = file.name.split('.').pop()?.toLowerCase();

  // ── TXT: fast path, no need to read as buffer ──
  if (extension === 'txt') {
    return readFileAsText(file);
  }

  // ── PDF / DOCX: read as ArrayBuffer, detect type from magic bytes ──
  const arrayBuffer = await readFileAsArrayBuffer(file);
  const detectedExt = detectFileTypeFromBuffer(arrayBuffer);

  console.log('Detected file type:', detectedExt);

  if (detectedExt === 'pdf') {
    return extractTextFromPdf(arrayBuffer);
  }

  if (detectedExt === 'docx') {
    return extractTextFromDocx(arrayBuffer);
  }

  throw new Error(`Unsupported document type: ${detectedExt ?? extension}`);
};

// ─── React Hook ──────────────────────────────────────────────────────────────

/**
 * useFileTextExtractor
 *
 * Drop-in hook that handles loading / error state around extractTextFromFile().
 *
 * Usage:
 * ```jsx
 * const { extractText, extractedText, isExtracting, error } = useFileTextExtractor();
 *
 * const handleFile = async (file) => {
 *   const text = await extractText(file);
 *   // send `text` to OpenAI / AssemblyAI / your translate endpoint
 * };
 * ```
 */
export const useFileTextExtractor = () => {
  const [extractedText, setExtractedText] = useState('');
  const [isExtracting,  setIsExtracting]  = useState(false);
  const [error,         setError]         = useState(null);

  const extractText = useCallback(async (file) => {
    if (!file) {
      setError('No file provided');
      return null;
    }

    setIsExtracting(true);
    setError(null);
    setExtractedText('');

    try {
      const text = await extractTextFromFile(file);
      setExtractedText(text);
      return text;                  // caller can also await the return value
    } catch (err) {
      console.error('Text extraction error:', err);
      setError(err.message);
      return null;
    } finally {
      setIsExtracting(false);
    }
  }, []);

  const reset = useCallback(() => {
    setExtractedText('');
    setError(null);
    setIsExtracting(false);
  }, []);

  return { extractText, extractedText, isExtracting, error, reset };
};

export { SUPPORTED_EXTENSIONS };